import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { listingSearchParam } from 'apis/handler/listing/listingSchema';

import type { MapPointsParam } from '../model/dto';
import { getListing, getListingMapPreview, getMapPoints, getSimilarListings } from './service';

export const getListingQueryKey = (searchParam: listingSearchParam) => {
  return ['listing', searchParam];
};

export const useGetListing = ({
  searchParam,
  enabled = true,
}: {
  searchParam: listingSearchParam;
  enabled?: boolean;
}) => {
  return useInfiniteQuery({
    queryKey: getListingQueryKey(searchParam),
    queryFn: ({ pageParam }) => getListing(searchParam, { page: pageParam, size: 10 }),
    initialPageParam: 0,
    // 서버가 결과 없이 실패를 돌려준 경우(예: 깨진 로그인 토큰)에도 페이지 전체가 죽지 않게 방어
    getNextPageParam: (lastPage) => (lastPage?.sliceInfo?.hasNext ? lastPage.sliceInfo.pageNumber + 1 : undefined),
    placeholderData: keepPreviousData,
    // 대학 랜딩의 미등록 슬러그처럼 파라미터가 무효일 때 낭비 요청을 막는 스위치 (기본 true — 기존 호출부 무영향)
    enabled,
  });
};

export const useGetListingMapPreview = (id: number | null) => {
  return useQuery({
    queryKey: ['listing', 'map-preview', id],
    queryFn: () => getListingMapPreview(id!),
    enabled: id !== null,
  });
};

export const useGetMapPoints = ({ params }: { params: MapPointsParam | null }) => {
  return useQuery({
    queryKey: ['listing', 'map-points', params],
    queryFn: () => getMapPoints(params!),
    enabled: params !== null,
    placeholderData: keepPreviousData,
  });
};

export const SIMILAR_LISTINGS_QUERY_KEY = ['listing', 'similar'];

export const useGetSimilarListings = (id: string) => {
  return useQuery({
    queryKey: [...SIMILAR_LISTINGS_QUERY_KEY, id],
    queryFn: () => getSimilarListings(id),
    // 부가 섹션이라 서버 장애여도 상세 페이지 전체를 에러 화면으로 바꾸지 않는다 (실패 시 섹션만 숨김)
    throwOnError: false,
  });
};
